import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useFriends } from '@/hooks/useFriends';
import { useUserTournamentsCount } from '@/hooks/useUserTournamentsCount';
import { useUserFollowersCount } from '@/hooks/useUserFollowersCount';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Settings, MapPin, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import FriendsNetwork from '@/components/profile/FriendsNetwork';
import { TournamentListDialog } from '@/components/profile/TournamentListDialog';
import { FollowersListDialog } from '@/components/profile/FollowersListDialog';
import { FollowingListDialog } from '@/components/profile/FollowingListDialog';
import { SettingsModal } from '@/components/profile/SettingsModal';

import EmbeddedAuth from '@/components/EmbeddedAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import PlayerTournamentStats from '@/components/profile/PlayerTournamentStats';
import TournamentHistory from '@/components/profile/TournamentHistory';
import ProfileRankingView from '@/components/profile/ProfileRankingView';

const ProfileContent = () => {
  const { user: authUser, loading } = useAuth();
  const { t } = useLanguage();
  const { profile } = useUserProfile();
  const { friends } = useFriends();
  const { tournamentsCount } = useUserTournamentsCount();
  const { followersCount } = useUserFollowersCount();
  const isMobile = useIsMobile();
  
  const [tournamentsDialogOpen, setTournamentsDialogOpen] = useState(false);
  const [followersDialogOpen, setFollowersDialogOpen] = useState(false);
  const [followingDialogOpen, setFollowingDialogOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const [activeProfileTab, setActiveProfileTab] = useState<'stats' | 'ranking'>('stats');

  const handleCopyId = async () => {
    if (!authUser?.id) return;
    try {
      await navigator.clipboard.writeText(authUser.id.slice(0, 8));
      setIdCopied(true);
      toast.success(t('profile.idCopied'));
      setTimeout(() => setIdCopied(false), 2000);
    } catch {
      toast.error(t('profile.idCopyError'));
    }
  };


  const favoriteSports = profile?.preferred_sports || ['Tenis', 'Pádel', 'Pickleball'];

  // Función para obtener el nombre del usuario con múltiples fallbacks
  const getDisplayName = () => {
    // 1. Priorizar full_name del perfil en BD
    if (profile?.full_name) {
      return profile.full_name;
    }
    
    // 2. Fallback a metadata del registro
    if (authUser?.user_metadata?.full_name) {
      return authUser.user_metadata.full_name as string;
    }
    
    // 3. Usar parte del email como nombre
    if (authUser?.email) {
      const emailName = authUser.email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    
    return t('profile.myProfile');
  };

  // Obtener iniciales para el avatar
  const getAvatarInitials = () => {
    const name = getDisplayName();
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('profile.loadingProfile')}</p>
        </div>
      </div>
    );
  }

  // Show login prompt if user is not authenticated
  if (!authUser) {
    return <EmbeddedAuth />;
  }

  return (
    <div className="min-h-[80vh] pt-0 px-0 pb-2 md:pb-6">
      {/* Profile Header - Compact & Modern */}
      <div className="px-4 py-4 md:px-6 md:py-6 bg-white w-full">
          {/* Avatar & Info - Horizontal Layout */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative shrink-0">
              <Avatar className="h-20 w-20 md:h-24 md:w-24 ring-2 ring-[#e7d1b6]">
                <AvatarImage src={profile?.avatar_url || ''} alt={profile?.full_name || ''} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-[#e7d1b6] text-xl font-semibold">
                  {getAvatarInitials()}
                </AvatarFallback>
              </Avatar>
            </div>

            <div className="flex-1 min-w-0 space-y-1">
              {/* Nombre + Settings en la misma fila */}
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-foreground truncate">
                    {getDisplayName()}
                  </h1>
                  {authUser?.id && (
                    <button
                      onClick={handleCopyId}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-0.5"
                    >
                      <span className="font-mono">ID: {authUser.id.slice(0, 8)}</span>
                      {idCopied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                    </button>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowSettings(true)}
                  className="shrink-0"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Deportes */}
              <div className="flex flex-wrap gap-1.5">
                {favoriteSports.slice(0, isMobile ? 2 : 3).map(sport => (
                  <Badge key={sport} variant="secondary" className="text-xs font-medium">
                    {sport}
                  </Badge>
                ))}
              </div>
              
              {/* Ubicación */}
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{profile?.location || 'México'}</span>
              </div>
            </div>
          </div>

          {/* Stats - Horizontal Scroll on Mobile */}
          <div className={`grid ${isMobile ? 'grid-cols-3 gap-2' : 'grid-cols-3 gap-4'}`}>
            <button
              onClick={() => setTournamentsDialogOpen(true)}
              className="flex flex-col items-center justify-center p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
            >
              <div className="text-2xl font-bold text-foreground">{tournamentsCount}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t('profile.tournaments')}</div>
            </button>

            <button
              onClick={() => setFollowersDialogOpen(true)}
              className="flex flex-col items-center justify-center p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
            >
              <div className="text-2xl font-bold text-foreground">{followersCount}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t('profile.followers')}</div>
            </button>

            <button
              onClick={() => setFollowingDialogOpen(true)}
              className="flex flex-col items-center justify-center p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
            >
              <div className="text-2xl font-bold text-foreground">{friends.length}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t('profile.friends')}</div>
            </button>
          </div>
      </div>

      {/* Tab Selector */}
      <div className="px-4 md:px-6 pt-4">
        <div className="flex gap-1 p-1 bg-muted rounded-lg">
          <button
            onClick={() => setActiveProfileTab('stats')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeProfileTab === 'stats'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('profile.statistics')}
          </button>
          <button
            onClick={() => setActiveProfileTab('ranking')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeProfileTab === 'ranking'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('profile.ranking')}
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="px-4 md:px-6 pt-4 pb-6 min-h-[50vh]">
        {activeProfileTab === 'stats' ? (
          <>
            <PlayerTournamentStats userId={authUser.id} />
            <TournamentHistory userId={authUser.id} />
            {!isMobile && <FriendsNetwork />}
          </>
        ) : (
          <ProfileRankingView userId={authUser.id} />
        )}
      </div>

      {/* Dialogs */}
      <TournamentListDialog 
        open={tournamentsDialogOpen} 
        onOpenChange={setTournamentsDialogOpen} 
      />
      <FollowersListDialog 
        open={followersDialogOpen} 
        onOpenChange={setFollowersDialogOpen} 
      />
      <FollowingListDialog 
        open={followingDialogOpen} 
        onOpenChange={setFollowingDialogOpen} 
      />
      <SettingsModal open={showSettings} onOpenChange={setShowSettings} />
      
    </div>
  );
};

export default ProfileContent;